import React, { memo, useState } from 'react'
import { Card, Progress, Spin, Statistic, Button } from 'antd'
import { DollarOutlined, ThunderboltOutlined, ShoppingCartOutlined, WalletOutlined, HistoryOutlined } from '@ant-design/icons'
import PurchaseHistoryModal from '../CreditReport/PurchaseHistoryModal'
import './SuperadminCostSummary.scss'

const fmt = (num, decimals = 2) => {
  if (num === null || num === undefined) return '—'
  const n = parseFloat(num)
  if (isNaN(n)) return '—'
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

const fmtCredits = (num) => {
  if (num === null || num === undefined) return '—'
  return Math.round(parseFloat(num) || 0).toLocaleString()
}

const SuperadminCostSummary = memo(({ data, loading }) => {
  const [historyVisible, setHistoryVisible] = useState(false)

  if (!data && !loading) return null

  const {
    totalCredits = 0,
    totalUsed = 0,
    remainingCredits = 0,
    consumedPercent = 0,
    creditValue = 0,
    purchasedCredits = 0,
    totalCreditCost = 0,
    totalCost = 0
  } = data || {}
  const displayPercent = typeof consumedPercent === 'number'
    ? (Number.isInteger(consumedPercent) ? consumedPercent : parseFloat(consumedPercent.toFixed(1)))
    : 0

  const metrics = [
    {
      key: 'purchased',
      icon: <ShoppingCartOutlined />,
      label: 'Credits Purchased',
      value: fmtCredits(purchasedCredits),
      accent: '#5B6EE2',
      bg: '#f0f1fd',
      action: (
        <div 
          className="scs-metric-sub"
          onClick={() => setHistoryVisible(true)}
          style={{ 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            marginTop: '4px',
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <HistoryOutlined /> View History
        </div>
      )
    },
    {
      key: 'remaining',
      icon: <WalletOutlined />,
      label: 'Remaining Credits',
      value: fmtCredits(remainingCredits),
      accent: '#3875F6',
      bg: '#eef3ff'
    },
    {
      key: 'used',
      icon: <ThunderboltOutlined />,
      label: 'Total Usage',
      value: fmtCredits(totalUsed) + ' credits',
      accent: '#EB3B88',
      bg: '#fef0f6'
    },
    {
      key: 'creditValue',
      icon: <DollarOutlined />,
      label: 'Total Credit Cost',
      value: `$${fmt(totalCreditCost)}`,
      sub: creditValue ? `@ $${creditValue} / credit` : null,
      accent: '#8E44AD',
      bg: '#F4ECF7'
    },
    {
      key: 'cost',
      icon: <DollarOutlined />,
      label: 'Actual Cost',
      value: `$${fmt(totalCost)}`,
      accent: '#44959A',
      bg: '#edf7f7'
    },


  ]

  return (
    <>
      <Card className="superadmin-cost-summary-card">
        <Spin spinning={loading}>
          <div className="scs-inner">
            <div className="scs-metrics">
              {metrics.map(m => (
                <div 
                  key={m.key} 
                  className="scs-metric-card" 
                  style={{ 
                    '--accent': m.accent, 
                    '--bg': m.bg
                  }}
                >
                  <div className="scs-metric-icon">{m.icon}</div>
                  <div className="scs-metric-body">
                    <span className="scs-metric-value">{m.value}</span>
                    <span className="scs-metric-label">{m.label}</span>
                    {m.sub && <span className="scs-metric-sub">{m.sub}</span>}
                    {m.action}
                  </div>
                </div>
              ))}
          </div>

          <div className="scs-progress-section">
            <div className="scs-progress-header">
              <span className="scs-progress-label">Credits Consumed</span>
              <span className="scs-progress-values">
                {fmtCredits(totalUsed)} of {fmtCredits(totalCredits)}
              </span>
            </div>
            <div className='scs-progress-bar-container'>
              <Progress
                percent={displayPercent}
                strokeColor={{
                  '0%': '#5B6EE2',
                  '100%': '#44959A'
                }}
                trailColor="#f0f0f0"
                showInfo={true}
                format={() => `${displayPercent}%`}
                className="scs-progress-bar"
                strokeWidth={10}
              />
            </div>
          </div>
          </div>
        </Spin>
      </Card>

      <PurchaseHistoryModal
        visible={historyVisible}
        onClose={() => setHistoryVisible(false)}
      />
    </>
  )
})

SuperadminCostSummary.displayName = 'SuperadminCostSummary'
export default SuperadminCostSummary
